import React, { useState, useRef } from "react";
import { NEW_PRINT } from "../config/config";
import print from "print-js";

function QuotationForm() {
  const [refNo, setRefNo] = useState("");
  const [toName, setToName] = useState("");
  const [address, setAddress] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [subject, setSubject] = useState("");
  const [station, setStation] = useState("");
  const [rate, setRate] = useState("");
  const [stationsList, setStationsList] = useState([]);
  const [faithfully, setFaithfully] = useState("");

  /* ---------- focus helpers ---------- */
  const inputRefs = useRef([]);
  const setInputRef = (el) => {
    if (el && !inputRefs.current.includes(el)) inputRefs.current.push(el);
  };
  const handleEnterFocus = (e, idx) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const next = inputRefs.current[idx + 1];
      next?.focus();
    }
  };

  /* ---------- station handling ---------- */
  const handleAddStation = () => {
    if (!station.trim() || !rate.trim()) return;
    setStationsList((prev) => [...prev, { station, rate }]);
    setStation("");
    setRate("");
    // jump back to Station input for rapid entry
    const stationInput = inputRefs.current.find(
      (el) => el?.dataset?.field === "station"
    );
    stationInput?.focus();
  };

  /* ---------- download/print ---------- */
  const handleDownload = async () => {
    const endpointUrl = `${NEW_PRINT}/bilty/print/`;
    const payload = { client_name: "rcc", file_name: "declarationform" };
    try {
      const response = await fetch(endpointUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) return console.error("Failed to send the request.");
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "quotationform.pdf";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Error during fetch:", err);
    }
  };

  const handlePrint = async () => {
    const endpointUrl = `${NEW_PRINT}/bilty/print/`;
    const payload = { client_name: "rcc", file_name: "quotationform", id: 1 };
    try {
      const blob = await fetch(endpointUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }).then((r) => r.blob());
      print({
        printable: URL.createObjectURL(blob),
        type: "pdf",
        showModal: false,
      });
    } catch (err) {
      console.error("Error during fetch:", err);
    }
  };

  /* ---------- inline styles ---------- */
  const rowStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: 10,
    gap: 10,
  };
  const labelStyle = { width: 120, fontWeight: "bold" };
  const inputStyle = { padding: 6, flex: 1 };
  const buttonStyle = {
    padding: "6px 12px",
    background: "#4CAF50",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
  };

  return (
    <div className="page-challan">
      <div className="form-header">Quotation Form</div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: 30,
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div
          style={{
            width: 600,
            padding: 20,
            border: "1px solid #c0b2b2",
            borderRadius: 8,
          }}
        >
          {/* fields */}
          <div style={rowStyle}>
            <label style={labelStyle}>Ref No:</label>
            <input
              ref={setInputRef}
              style={inputStyle}
              value={refNo}
              onChange={(e) => setRefNo(e.target.value)}
              onKeyDown={(e) => handleEnterFocus(e, 0)}
            />
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Date:</label>
            <input
              type="date"
              ref={setInputRef}
              style={inputStyle}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              onKeyDown={(e) => handleEnterFocus(e, 1)}
            />
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>To:</label>
            <input
              ref={setInputRef}
              style={inputStyle}
              value={toName}
              onChange={(e) => setToName(e.target.value)}
              onKeyDown={(e) => handleEnterFocus(e, 2)}
            />
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Address:</label>
            <input
              ref={setInputRef}
              style={inputStyle}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onKeyDown={(e) => handleEnterFocus(e, 3)}
            />
          </div>

          <div style={rowStyle}>
            <label style={labelStyle}>Subject:</label>
            <textarea
              ref={setInputRef}
              style={{ ...inputStyle, height: 80, resize: "vertical" }}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onKeyDown={(e) => handleEnterFocus(e, 4)}
            />
          </div>

          {/* Station & Rate */}
          <div style={rowStyle}>
            <label style={labelStyle}>Station:</label>
            <input
              data-field="station"
              ref={setInputRef}
              style={inputStyle}
              value={station}
              onChange={(e) => setStation(e.target.value)}
              onKeyDown={(e) => handleEnterFocus(e, 5)}
              placeholder=""
            />
            <label style={labelStyle}>Rate:</label>
            <input
              ref={setInputRef}
              style={{ ...inputStyle, width: 80 }}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddStation();
                }
              }}
              placeholder=""
            />
            <button
              style={buttonStyle}
              type="button"
              onClick={handleAddStation}
            >
              Add
            </button>
          </div>

          {/* Dynamic Table */}
          {stationsList.length > 0 && (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: 10,
              }}
            >
              <thead>
                <tr>
                  <th style={{ border: "1px solid #ccc", padding: 8 }}>
                    Station
                  </th>
                  <th style={{ border: "1px solid #ccc", padding: 8 }}>Rate</th>
                  <th style={{ border: "1px solid #ccc", padding: 8 }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {stationsList.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ border: "1px solid #ccc", padding: 6 }}>
                      {item.station.charAt(0).toUpperCase() +
                        item.station.slice(1)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: 6 }}>
                      {item.rate}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: 6 }}>
                      <button
                        style={{
                          ...buttonStyle,
                          background: "#f44336",
                          padding: "4px 8px",
                        }}
                        type="button"
                        onClick={() =>
                          setStationsList((prev) =>
                            prev.filter((_, i) => i !== idx)
                          )
                        }
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ ...rowStyle, marginTop: 20 }}>
            <label style={labelStyle}>Yours Faithfully:</label>
            <input
              ref={setInputRef}
              style={inputStyle}
              value={faithfully}
              onChange={(e) => setFaithfully(e.target.value)}
              onKeyDown={(e) => handleEnterFocus(e, 6)}
            />
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 20,
              marginTop: 30,
            }}
          >
            <button 
              style={buttonStyle} 
              // onClick={handleDownload}
            >
              Download Quotation
            </button>
            <button
              style={{ ...buttonStyle, background: "#2196F3" }}
              // onClick={handlePrint}
            >
              Print Quotation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuotationForm;
